import { captureRemixErrorBoundaryError } from "@sentry/remix";
import {
  Link,
  Links,
  Meta,
  Outlet,
  Scripts,
  ScrollRestoration,
  useRouteError,
} from "@remix-run/react";
import { LiveblocksProvider } from "@liveblocks/react/suspense";
import { Slide, ToastContainer } from "react-toastify";
import { rootAuthLoader } from "@clerk/remix/ssr.server";
import { ClerkApp, useAuth, useUser } from "@clerk/remix";
import { LoaderFunction } from "@remix-run/node";

import "./tailwind.css";
import "./global.css";
import "react-toastify/dist/ReactToastify.min.css";
import "@fontsource-variable/raleway/wght.css";
import "@fontsource-variable/rokkitt/wght.css";
import { useEffect, useState } from "react";
import { posthog } from "posthog-js";
import * as Sentry from "@sentry/remix";
import { Button } from "./components/common/button";
import { FeedbackDialog } from "./components/feedback-dialog";

export const loader: LoaderFunction = (args) => rootAuthLoader(args);

export function Layout({ children }: { children: React.ReactNode }) {
  return (
    <html lang="en" suppressHydrationWarning>
      <head>
        <script
          dangerouslySetInnerHTML={{
            __html: `
        try {
          if (localStorage.theme === 'dark' || (!('theme' in localStorage) && window.matchMedia('(prefers-color-scheme: dark)').matches && !window.location.pathname.startsWith('/external'))) {
            document.documentElement.classList.add('dark')
          } else {
            document.documentElement.classList.remove('dark')
          }
        } catch (_) {}
      `,
          }}
        />
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <Meta />
        <Links />
        <script
          dangerouslySetInnerHTML={{
            __html: `
              window.dataLayer = window.dataLayer || [];
              function gtag(){dataLayer.push(arguments);}
              gtag('js', new Date());
              gtag('config', 'AW-17386122090');
                  `,
          }}
        />
      </head>
      <body className="font-mono">
        {children}
        <ScrollRestoration />
        <Scripts />
        <ToastContainer
          position="bottom-right"
          autoClose={5000}
          hideProgressBar
          newestOnTop={false}
          closeOnClick
          rtl={false}
          pauseOnFocusLoss
          draggable
          pauseOnHover
          theme="light"
          transition={Slide}
        />
      </body>
    </html>
  );
}

const App = () => {
  const { isSignedIn, userId } = useAuth();
  const { user } = useUser();

  useEffect(() => {
    // 👉 Check the sign in status and user info,
    //    and identify the user if they aren't already
    if (isSignedIn && userId && user && !posthog._isIdentified()) {
      // 👉 Identify the user
      posthog.identify(userId, {
        email: user.primaryEmailAddress?.emailAddress,
        username: user.username,
      });

      Sentry.setUser({
        id: userId,
        email: user.primaryEmailAddress?.emailAddress,
        username: user.username || user.fullName || "",
      });
    }

    // 👉 Reset the user if they sign out
    if (!isSignedIn && posthog._isIdentified()) {
      posthog.reset();
      Sentry.setUser(null);
    }
  }, [isSignedIn, user, userId]);

  return (
    <>
      <LiveblocksProvider authEndpoint="/api/liveblocks-auth">
        <Outlet />
      </LiveblocksProvider>
    </>
  );
};

export const ErrorBoundary = () => {
  const error: any = useRouteError();
  captureRemixErrorBoundaryError(error);
  const [isFeedbackDialogOpen, setIsFeedbackDialogOpen] = useState(false);

  console.error(error);

  return (
    <div className="min-h-screen flex items-center justify-center bg-brandBackground dark:bg-zinc-900">
      <div className="text-center">
        <h1 className="text-4xl font-bold text-gray-900 mb-4 dark:text-brandWhite">
          {error?.status === 404
            ? "Oops! That page doesn't exist"
            : "Oh no! Something went wrong"}
        </h1>
        <p className="text-gray-600 mb-8 dark:text-brandWhite/80">
          {error?.status === 404
            ? "Check the URL and try again."
            : `We're sorry, but an unexpected error (${
                error?.status || "unknown"
              }) occurred.`}
        </p>
        <div className="space-x-4">
          <Button onClick={() => setIsFeedbackDialogOpen(true)}>
            Send Feedback
          </Button>
          <Link to="/" className="text-brandPrimary dark:text-white">
            Go Home
          </Link>
        </div>
        <FeedbackDialog
          isOpen={isFeedbackDialogOpen}
          setIsOpen={setIsFeedbackDialogOpen}
        />
      </div>
    </div>
  );
};

export default ClerkApp(App, {
  appearance: {
    organizationSwitcher: { variables: { fontSize: "0.85rem" } },
  },
});
